import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import Footer from "../components/Footer";
import EsitoBadge from "../components/EsitoBadge";
import api from "../lib/api";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | in_vendita | venduto
  const [provincia, setProvincia] = useState("");
  const [comune, setComune] = useState("");
  useEffect(() => { api.get("/public/properties").then((r) => setItems(r.data)).finally(() => setLoading(false)); }, []);

  const photoUrl = (p) => p?.photos?.[0]?.external_url || (p?.photos?.[0]?.path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.photos[0].path}` : null);

  const province = useMemo(
    () => Array.from(new Set(items.map((p) => p.provincia).filter(Boolean))).sort(),
    [items]
  );
  const comuni = useMemo(() => {
    const src = provincia ? items.filter((p) => p.provincia === provincia) : items;
    return Array.from(new Set(src.map((p) => p.comune).filter(Boolean))).sort();
  }, [items, provincia]);

  const geoFiltered = useMemo(() => {
    return items.filter((p) => {
      if (provincia && p.provincia !== provincia) return false;
      if (comune && p.comune !== comune) return false;
      return true;
    });
  }, [items, provincia, comune]);

  const counts = useMemo(() => ({
    all: geoFiltered.length,
    in_vendita: geoFiltered.filter((p) => p.stato_pubblicazione !== "venduto").length,
    venduto: geoFiltered.filter((p) => p.stato_pubblicazione === "venduto").length,
  }), [geoFiltered]);
  const filtered = useMemo(() => {
    if (filter === "in_vendita") return geoFiltered.filter((p) => p.stato_pubblicazione !== "venduto");
    if (filter === "venduto") return geoFiltered.filter((p) => p.stato_pubblicazione === "venduto");
    return geoFiltered;
  }, [geoFiltered, filter]);

  const resetFilters = () => { setFilter("all"); setProvincia(""); setComune(""); };
  const hasActiveFilters = filter !== "all" || provincia || comune;

  return (
    <div className="paper-bg" data-testid="gallery-page">
      <PublicNav />
      <section className="max-w-[1240px] mx-auto px-6 md:px-10 pt-16 pb-6">
        <div className="label-uppercase">La vetrina della categoria</div>
        <h1 className="font-serif text-5xl md:text-6xl mt-3">Galleria Pronto alla Vendita.</h1>
        <p className="text-[#5C5657] mt-4 max-w-2xl">Ogni immobile in Galleria ha ricevuto un esito positivo del Protocollo — Pronto alla Vendita o con Criticità Dichiarate. Le criticità Rilevanti, quando presenti, sono dichiarate in scheda prima ancora della trattativa.</p>
      </section>

      {/* Filtri: territorio + stato pubblicazione */}
      <section className="max-w-[1240px] mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-[#E6E4E0] pb-5">
          <span className="label-uppercase text-[#5C5657]">Territorio:</span>
          <select
            id="gallery-provincia"
            data-testid="gallery-filter-provincia"
            value={provincia}
            onChange={(e) => { setProvincia(e.target.value); setComune(""); }}
            className="text-sm border border-[#E6E4E0] bg-white px-3 py-2 min-w-[180px] focus:border-[#7D2E3F] focus:outline-none"
          >
            <option value="">Tutte le province</option>
            {province.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            id="gallery-comune"
            data-testid="gallery-filter-comune"
            value={comune}
            onChange={(e) => setComune(e.target.value)}
            disabled={comuni.length === 0}
            className="text-sm border border-[#E6E4E0] bg-white px-3 py-2 min-w-[180px] focus:border-[#7D2E3F] focus:outline-none disabled:opacity-50"
          >
            <option value="">{provincia ? "Tutti i comuni della provincia" : "Tutti i comuni"}</option>
            {comuni.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          <span className="hidden md:inline-block w-px h-6 bg-[#E6E4E0] mx-2" aria-hidden />

          <span className="label-uppercase text-[#5C5657]">Immobili:</span>
          {[
            { v: "all", l: "Tutti", n: counts.all },
            { v: "in_vendita", l: "Disponibili", n: counts.in_vendita },
            { v: "venduto", l: "Venduti", n: counts.venduto },
          ].map((t) => (
            <button
              key={t.v}
              data-testid={`gallery-filter-${t.v}`}
              onClick={() => setFilter(t.v)}
              className={`text-xs label-uppercase px-3 py-2 border transition-colors ${
                filter === t.v
                  ? "border-[#7D2E3F] text-[#7D2E3F] bg-[#FDFDFC]"
                  : "border-[#E6E4E0] text-[#5C5657] hover:text-[#3A3D42]"
              }`}
            >
              {t.l} <span className="opacity-60">({t.n})</span>
            </button>
          ))}

          {hasActiveFilters && (
            <button
              data-testid="gallery-reset-filters"
              onClick={resetFilters}
              className="text-xs label-uppercase text-[#B3261E] hover:underline ml-auto"
            >
              × Azzera filtri
            </button>
          )}
        </div>
      </section>

      <section className="max-w-[1240px] mx-auto px-6 md:px-10 pt-10 pb-24">
        {(() => {
          const soldRecent = geoFiltered
            .filter((p) => p.stato_pubblicazione === "venduto")
            .sort((a, b) => String(b.data_vendita || "").localeCompare(String(a.data_vendita || "")))
            .slice(0, 6);
          if (filter !== "all" || soldRecent.length === 0) return null;
          const fmt = (iso) => {
            if (!iso) return "";
            const s = String(iso).slice(0, 10).split("-");
            return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : iso;
          };
          return (
            <div className="mb-10 border-t border-b border-[#E6E4E0] py-6" data-testid="sold-timeline">
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="label-uppercase text-[#5C5657]">Track record</div>
                  <h2 className="font-serif text-2xl mt-1">Ultimi immobili venduti</h2>
                </div>
                <button onClick={() => setFilter("venduto")} className="text-xs label-uppercase text-[#7D2E3F] hover:underline">Vedi tutti →</button>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-2 -mx-2 px-2" data-testid="sold-timeline-strip">
                {soldRecent.map((p) => (
                  <Link
                    to={`/immobile/${p.id}`}
                    key={p.id}
                    data-testid={`sold-strip-${p.id}`}
                    className="shrink-0 w-56 group"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-[#F4F3F1] border border-[#E6E4E0]">
                      {photoUrl(p) ? (
                        <img src={photoUrl(p)} alt={p.titolo} className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#5C5657] text-xs">Nessuna foto</div>
                      )}
                      <span className="absolute top-2 right-2 bg-[#3A3D42] text-white text-[10px] tracking-[0.2em] uppercase px-2 py-0.5">Venduto</span>
                    </div>
                    <div className="mt-2 text-sm font-medium text-[#1A1617] truncate">{p.titolo}</div>
                    <div className="text-xs text-[#5C5657]">{p.comune}{p.data_vendita ? ` · ${fmt(p.data_vendita)}` : ""}</div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })()}
        {loading ? (
          <div className="py-20 text-center text-[#5C5657]">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-[#E6E4E0] py-20 text-center text-[#5C5657]" data-testid="gallery-empty">
            <div className="label-uppercase mb-2">Nessun immobile</div>
            <p>{filter === "all" ? "La Galleria verrà popolata non appena i primi immobili avranno superato il Protocollo." : "Nessun immobile corrisponde a questo filtro."}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14" data-testid="gallery-grid">
            {filtered.map((p) => (
              <Link to={`/immobile/${p.id}`} key={p.id} data-testid={`property-card-${p.id}`} className="group flex flex-col border border-transparent hover:border-[#E6E4E0] transition-colors">
                <div className="relative aspect-[4/5] overflow-hidden bg-[#F4F3F1] border border-[#E6E4E0]">
                  {photoUrl(p) ? (
                    <img src={photoUrl(p)} alt={p.titolo} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#5C5657]">Nessuna foto</div>
                  )}
                  {p.stato_pubblicazione === "venduto" ? (
                    <span
                      data-testid={`venduto-tag-${p.id}`}
                      className="absolute top-3 right-3 bg-[#3A3D42] text-white text-xs tracking-[0.2em] uppercase px-3 py-1 shadow-sm"
                    >
                      Venduto
                    </span>
                  ) : (
                    <>
                      {(p.esito === "pronto" || p.esito === "criticita") && (
                        <img
                          src="/brand/bollino-512.png"
                          alt="Pronto alla Vendita"
                          title={p.esito === "criticita" ? "Pronto alla Vendita con Criticità Dichiarate" : "Pronto alla Vendita"}
                          className="absolute top-3 right-3 w-12 h-12 md:w-14 md:h-14 [filter:drop-shadow(0_2px_4px_rgba(0,0,0,0.22))_drop-shadow(0_6px_14px_rgba(0,0,0,0.28))]"
                          data-testid={`gallery-bollino-${p.id}`}
                        />
                      )}
                      <span
                        data-testid={`disponibile-tag-${p.id}`}
                        className="absolute bottom-3 left-3 bg-white/90 text-[#3A3D42] text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 border border-[#E6E4E0]"
                      >
                        Disponibile
                      </span>
                    </>
                  )}
                </div>
                <div className="mt-5 flex-1 flex flex-col">
                  <div className="label-uppercase">{p.tipologia} · {p.comune}</div>
                  <h3 className="font-serif text-2xl mt-1 leading-tight group-hover:text-[#7D2E3F] min-h-[4rem]">{p.titolo}</h3>
                  <div className="text-sm text-[#5C5657] mt-2">
                    {[
                      p.superficie ? `${p.superficie} m²` : null,
                      p.locali ? `${p.locali} local${p.locali === 1 ? "e" : "i"}` : null,
                      p.piano ? `Piano ${p.piano}` : null,
                    ].filter(Boolean).join(" · ") || "—"}
                  </div>
                  <div className="mt-4 flex-1"><EsitoBadge esito={p.esito} testId={`badge-${p.id}`} /></div>
                  <div className="mt-5 pt-4 border-t border-[#E6E4E0] flex items-center justify-between">
                    <span className="text-xs font-semibold tracking-[0.15em] uppercase !text-[#7D2E3F]" style={{color: "#7D2E3F"}}>Vedi la scheda immobile</span>
                    <span className="text-[#7D2E3F] group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
